import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';

import { MANDATE_INTERNAL_STATUSES, type MandateInternalStatus } from '@solar/contracts';

import {
  AdminIcon,
  ManagementHeader,
  MetricCard,
  adminManagementStyles as styles,
  conciseId,
  formatAdminDate,
} from '../../../components/admin/AdminManagement';
import { Alert } from '../../../components/Alert';
import { mandatesApi } from '../../../features/mandates/mandates.api';
import { operationalApi } from '../../../features/reporting/operational.api';
import { toDisplayMessage } from '../../../lib/error-message';
import { formatNgn } from '../../../lib/format-currency';
import { formatStatusLabel } from '../../../lib/format-status';
import { routeTo } from '../../../routes/paths';

function statusClass(status: string): string {
  if (status === 'ACTIVE') return styles.success ?? '';
  if (status === 'FAILED' || status === 'EXPIRED') return styles.danger ?? '';
  return styles.warning ?? '';
}

export function AdminMandatesPage(): React.JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState<MandateInternalStatus | ''>(() => {
    const requested = searchParams.get('status');
    return requested !== null &&
      MANDATE_INTERNAL_STATUSES.includes(requested as MandateInternalStatus)
      ? (requested as MandateInternalStatus)
      : '';
  });
  const mandates = useQuery({
    queryKey: ['admin', 'mandates', status],
    queryFn: () => mandatesApi.listAdmin({ pageSize: 50, internalStatus: status || undefined }),
  });
  const summary = useQuery({
    queryKey: ['admin', 'mandates', 'summary'],
    queryFn: () => operationalApi.mandateSummary(),
  });
  const queue = useQuery({
    queryKey: ['admin', 'mandates', 'activation-queue'],
    queryFn: () => operationalApi.mandateActivationQueue(1, 5),
  });
  const items = mandates.data?.items ?? [];

  return (
    <div className={styles.page}>
      <ManagementHeader
        eyebrow="Direct debit operations"
        title="Mandates"
        subtitle="Monitor activation and resolve provider authorization exceptions."
        icon="bank"
        context="Mandate operations"
      />
      <section className={styles.hero}>
        <div>
          <h2>Keep mandate activation moving</h2>
          <p>Prioritize pending and failed mandates using provider-safe operational data.</p>
        </div>
        <strong>
          {summary.data ? `${summary.data.pendingActivation} awaiting` : 'Activation queue'}
        </strong>
      </section>
      <section className={styles.metrics}>
        <MetricCard
          label="Pending activation"
          value={summary.data?.pendingActivation ?? '—'}
          icon="clock"
        />
        <MetricCard label="Active" value={summary.data?.active ?? '—'} icon="check" tone="blue" />
        <MetricCard
          label="Failed"
          value={summary.data?.failed ?? '—'}
          icon="warning"
          tone="amber"
        />
        <MetricCard
          label="Open cancellations"
          value={summary.data?.cancellationOpen ?? '—'}
          icon="document"
          tone="violet"
        />
      </section>
      <form className={styles.filterPanel} aria-label="Filter mandates">
        <label>
          Status
          <select
            value={status}
            onChange={(event) => {
              const value = event.target.value as MandateInternalStatus | '';
              setStatus(value);
              setSearchParams(value === '' ? {} : { status: value });
            }}
          >
            <option value="">All statuses</option>
            {MANDATE_INTERNAL_STATUSES.map((value) => (
              <option value={value} key={value}>
                {formatStatusLabel(value)}
              </option>
            ))}
          </select>
        </label>
      </form>
      <div className={styles.contentGrid}>
        <section className={styles.mainCard}>
          <header className={styles.sectionHeader}>
            <div className={styles.sectionTitle}>
              <span className={styles.sectionIcon}>
                <AdminIcon name="bank" />
              </span>
              <div>
                <h2>Mandate register</h2>
                <p>Safe provider and activation information</p>
              </div>
            </div>
            {mandates.data && (
              <span className={styles.countBadge}>
                {mandates.data.pagination.totalItems} mandates
              </span>
            )}
          </header>
          {mandates.isPending && <p className={styles.empty}>Loading mandates…</p>}
          {mandates.isError && (
            <div className={styles.alertWrap}>
              <Alert tone="error">{toDisplayMessage(mandates.error)}</Alert>
            </div>
          )}
          {mandates.isSuccess && items.length === 0 && (
            <p className={styles.empty}>No mandates match this status.</p>
          )}
          {items.length > 0 && (
            <div className={styles.tableWrap}>
              <table className={styles.table} aria-label="Mandates">
                <thead>
                  <tr>
                    <th>Mandate</th>
                    <th>Customer</th>
                    <th>Contract</th>
                    <th>Status</th>
                    <th>Provider</th>
                    <th>Activation amount</th>
                    <th>Created</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((mandate) => (
                    <tr key={mandate.id}>
                      <td>
                        <div className={styles.primaryCell}>
                          <span className={styles.avatar}>
                            <AdminIcon name="bank" />
                          </span>
                          <span>
                            <strong>{mandate.mandateCode ?? conciseId(mandate.id)}</strong>
                            <small>Direct debit mandate</small>
                          </span>
                        </div>
                      </td>
                      <td>
                        {queue.data?.items.find((item) => item.mandateId === mandate.id)
                          ?.customerDisplayName ?? conciseId(mandate.customerId)}
                      </td>
                      <td>{conciseId(mandate.subscriptionId)}</td>
                      <td>
                        <span className={`${styles.status} ${statusClass(mandate.internalStatus)}`}>
                          {formatStatusLabel(mandate.internalStatus)}
                        </span>
                      </td>
                      <td>{mandate.provider}</td>
                      <td>
                        {mandate.activationAmount === null
                          ? '—'
                          : formatNgn(mandate.activationAmount)}
                      </td>
                      <td>{formatAdminDate(mandate.createdAt)}</td>
                      <td>
                        <Link to={routeTo.adminMandate(mandate.id)}>Review →</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <aside className={styles.rail}>
          <section className={styles.railCard}>
            <h2>Activation queue</h2>
            <p>Days measure time since the mandate’s last update.</p>
            <ul className={styles.railList}>
              {queue.data?.items.map((item) => (
                <li key={item.mandateId}>
                  <span>
                    <i className={styles.dot} />
                    {item.customerDisplayName}
                  </span>
                  <Link to={item.detailRoute}>{item.daysSinceLastUpdate}d</Link>
                </li>
              ))}
              {queue.data?.items.length === 0 && <li>Queue is clear</li>}
            </ul>
          </section>
          <section className={styles.railCard}>
            <h2>Status distribution</h2>
            <ul className={styles.railList}>
              {summary.data?.byStatus.map((item) => (
                <li key={item.status}>
                  <span>{formatStatusLabel(item.status)}</span>
                  <strong>{item.count}</strong>
                </li>
              ))}
            </ul>
          </section>
          <section className={styles.railCard}>
            <h2>Operational guidance</h2>
            <p>
              Use mandate detail to check current provider status. Account numbers and raw provider
              payloads are never displayed here.
            </p>
          </section>
        </aside>
      </div>
    </div>
  );
}
